// ResourceDetailCard — the "Resource" card inside the task drawer. A composable
// shell: a head (logo + name + "Catalog Item: X · Catalog: Y" sub-line) plus a
// stack of label/value rows supplied by the caller via `ResourceRow` children.
// design ref: styles.css:1265 (.resource / .resource__head / .resource__sub /
// .resource__row) + drawers.jsx AccessRequestResource / ProvisioningResource.

import { Box, Flex, HStack, Text } from "@chakra-ui/react";
import { ReactNode } from "react";

import AppLogo from "components/ds/AppLogo";

export interface ResourceRowProps {
  label: string;
  /** Top-align the label with a multi-line value (e.g. the team-adoption bar). */
  alignTop?: boolean;
  children: ReactNode;
}

// .resource__row — 12px 16px padding, 140px label, hairline divider, 13px.
export const ResourceRow = ({ label, alignTop, children }: ResourceRowProps) => (
  <Flex
    align={alignTop ? "flex-start" : "center"}
    gap="md"
    px="lg"
    py="md"
    borderBottomWidth="1px"
    borderColor="neutral.100"
    fontSize="smd"
    _last={{ borderBottom: "none" }}
  >
    <Box w="140px" flexShrink={0} color="neutral.700" pt={alignTop ? "2px" : 0}>
      {label}
    </Box>
    <Box flex="1" minW={0} color="neutral.950" fontWeight={500}>
      {children}
    </Box>
  </Flex>
);

export interface ResourceDetailCardProps {
  name: string;
  imageUrl?: string | null;
  catalogItemName?: string | null;
  catalogName?: string | null;
  /** The label/value rows — composed by the caller with `ResourceRow`. */
  children?: ReactNode;
}

const ResourceDetailCard = ({
  name,
  imageUrl,
  catalogItemName,
  catalogName,
  children,
}: ResourceDetailCardProps) => {
  const hasSub = Boolean(catalogItemName || catalogName);

  return (
    <Box
      borderWidth="1px"
      borderColor="neutral.200"
      borderRadius="10px" // design: .resource is 10px (no token at 10px)
      bg="neutral.25"
      overflow="hidden"
    >
      {/* .resource__head */}
      <HStack
        spacing="md"
        px="lg"
        py="14px"
        borderBottomWidth="1px"
        borderColor="neutral.100"
      >
        <AppLogo name={name} logoUrl={imageUrl ?? undefined} size="md" />
        <Box minW={0}>
          <Text fontSize="md" fontWeight={600} color="neutral.950" lineHeight="1" noOfLines={1}>
            {name}
          </Text>
          {hasSub && (
            <Flex gap="2xs" mt="2px" color="neutral.700" flexWrap="wrap" align="center">
              {catalogItemName && (
                <Box as="span" fontSize="md">
                  Catalog Item:{" "}
                  <Box as="span" fontWeight={500} color="neutral.950">
                    {catalogItemName}
                  </Box>
                </Box>
              )}
              {catalogItemName && catalogName && (
                <Box as="span" fontSize="xs">
                  ·
                </Box>
              )}
              {catalogName && (
                <Box as="span" fontSize="md">
                  Catalog:{" "}
                  <Box as="span" fontWeight={500} color="neutral.950">
                    {catalogName}
                  </Box>
                </Box>
              )}
            </Flex>
          )}
        </Box>
      </HStack>

      {children}
    </Box>
  );
};

export default ResourceDetailCard;
